/** @file fileMenu: File: New project, New puppet, Import bundle and template clips, Export, Sign out. */
import type { EditorState } from '../state/editorTypes';
import { bakedQuery } from './bakedQuery';
import { EDITOR_SETTINGS } from './editorSettings';
import { exportPuppet } from './exportPuppet';
import type { BarItem, MenuBuilder } from './menuTypes';
import { signOut } from './signOut';

/** The project New puppet would join: the routed project, else the open puppet's. */
const projectOf = (s: EditorState): string | null => (s.route.screen === 'puppets' ? s.route.projectId : s.puppet.projectId);

/** Focuses the New project name field once the Projects screen has drawn it. */
function focusNewProject(): void {
  const focus = (): void => { document.querySelector<HTMLInputElement>('input[aria-label="New project name"]')?.focus(); };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(focus);
  else setTimeout(focus, 0);
}

/** The two export rows, the default format (Settings) first. */
function exportItems(ctx: Parameters<MenuBuilder>[0], puppet: { id: string; name: string } | null): BarItem[] {
  const defaults = EDITOR_SETTINGS.export.load();
  const off = ctx.api === null || puppet === null;
  const run = (baked: boolean) => (): void => {
    if (puppet === null) return;
    const query = baked ? bakedQuery({ directions: defaults.directions, scale: defaults.scale, trim: true, grid: '' }) : undefined;
    exportPuppet(ctx.store, ctx.api, puppet.id, puppet.name, query);
  };
  const bundle: BarItem = { kind: 'item', id: 'file.exportBundle', label: 'Export runtime bundle (zip)', disabled: off, onSelect: run(false) };
  const baked: BarItem = { kind: 'item', id: 'file.exportBaked', label: 'Export baked sprite sheets (zip)', disabled: off, onSelect: run(true) };
  return defaults.format === 'baked' ? [baked, bundle] : [bundle, baked];
}

/** File > Import template clips…: the store's action on the open puppet (needs a server, a puppet and the action). */
function importClipsItem(ctx: Parameters<MenuBuilder>[0], puppet: { id: string } | null): BarItem {
  const action = ctx.store.getState().importTemplateClips;
  const off = ctx.api === null || puppet === null || action === undefined;
  return {
    kind: 'item', id: 'file.importTemplateClips', label: 'Import template clips…', disabled: off,
    hint: puppet === null ? 'Open a puppet first' : undefined, onSelect: () => { if (!off) void action(); },
  };
}

/** File menu rows; exports and the clip import need a server and an open puppet, New puppet needs a project in view. */
export const fileMenu: MenuBuilder = (ctx) => {
  const s = ctx.store.getState();
  const project = projectOf(s);
  const puppet = s.puppet.id === null ? null : { id: s.puppet.id, name: s.puppet.doc?.name ?? s.puppet.id };
  return [
    { kind: 'item', id: 'file.newProject', label: 'New project', disabled: ctx.api === null, onSelect: () => { s.navigate({ screen: 'projects' }); focusNewProject(); } },
    {
      kind: 'item', id: 'file.newPuppet', label: 'New puppet...', disabled: ctx.api === null || project === null,
      hint: project === null ? 'Open a project first' : undefined,
      onSelect: () => { if (project !== null) { s.navigate({ screen: 'puppets', projectId: project }); s.setShell({ dialog: 'newPuppet' }); } },
    },
    { kind: 'item', id: 'file.import', label: 'Import bundle...', disabled: true, hint: 'The server has no import yet', onSelect: () => undefined },
    importClipsItem(ctx, puppet),
    { kind: 'separator' },
    ...exportItems(ctx, puppet),
    { kind: 'separator' },
    { kind: 'item', id: 'file.signOut', label: 'Sign out', disabled: ctx.api === null, onSelect: () => { signOut(ctx.store, ctx.api); } },
  ];
};
